import { DefaultTheme } from "styled-components"

export const themeSettings = {
  modes: {
    light: {
      mode: "light",

      // Core Brand Colors
      primary: "#DF9162",
      primaryLight: "#F9D3BA",
      primaryDark: "#A66438",

      // Accent & Secondary Colors
      secondary: "#4A4A4A",
      tertiary: "#969696",
      highlight: "#FFE07D",

      // Status Colors
      success: "#31A66D",
      warning: "#E0A431",
      danger: "#D93741",
      info: "#2A8D9C",

      // Neutral Colors
      white: "#FFFFFF",
      black: "#000000",
      grey: "#969696",
      lightGrey: "#F9F9F7",
      darkGrey: "#4A4A4A",

      // Text Colors
      primaryText: "#525252",
      secondaryText: "#4A4A4A",
      link: "#247BA0",

      // Background Colors
      backgroundPrimary: "#F9F9F7",
      backgroundSecondary: "#EDEDEB",
      backgroundTertiary: "#FFFFFF",
    },

    dark: {
      mode: "dark",

      // Core Brand Colors (Dark Mode Variants)
      primary: "#1E4A32",
      primaryLight: "#3E7B57",
      primaryDark: "#142F22",

      // Accent & Secondary Colors (Dark Mode)
      secondary: "#969696",
      tertiary: "#4A4A4A",
      highlight: "#FFC861",

      // Status Colors
      success: "#2D8A5F",
      warning: "#B97D29",
      danger: "#C42D37",
      info: "#1D6F7D",

      // Neutral Colors
      white: "#FFFFFF",
      black: "#000000",
      grey: "#969696",
      lightGrey: "#3A3A3A",
      darkGrey: "#171717",

      // Text Colors
      primaryText: "#F9F9F7",
      secondaryText: "#969696",
      link: "#78DDE2",

      // Background Colors (Dark Mode)
      backgroundPrimary: "#0F1012",
      backgroundSecondary: "#171717",
      backgroundTertiary: "#282A44",
    },
  },

  // Shared Typography Tokens (using rem units)
  typography: {
    fontFamily: "Nunito Sans, Arial, sans-serif",
    fontSize: {
      xs: "0.75rem", // 12px
      sm: "0.875rem", // 14px
      md: "1rem", // 16px
      lg: "1.125rem", // 18px
      xl: "1.5rem", // 24px
      xxl: "2rem", // 32px
    },
    fontWeight: {
      regular: 400,
      medium: 500,
      bold: 700,
    },
  },

  // Spacing Scale (using rem units)
  spacing: {
    xs: "0.25rem", // 4px
    sm: "0.5rem", // 8px
    md: "1rem", // 16px
    lg: "1.5rem", // 24px
    xl: "2rem", // 32px
    xxl: "3rem", // 48px
  },

  // Breakpoints
  breakpoints: {
    xs: "0px",
    sm: "36rem", // 576px
    md: "48rem", // 768px
    lg: "62rem", // 992px
    xl: "75rem", // 1200px
    tablet: "48rem",
    desktop: "62rem",
    widescreen: "75rem",
    extraWidescreen: "87.5rem", // 1400px
  },

  // Component-Level Tokens
  components: {
    borderRadius: {
      small: "0.25rem", // 4px
      medium: "0.5rem", // 8px
      large: "0.75rem", // 12px
      round: "50%",
    },
    boxShadow: {
      small: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.1)", // 0px 2px 4px
      medium: "0 0.25rem 0.5rem rgba(0, 0, 0, 0.15)", // 0px 4px 8px
      large: "0 0.375rem 0.75rem rgba(0, 0, 0, 0.2)", // 0px 6px 12px
    },
    transitionSpeed: "400ms",
  },

  // Z-Index Layers
  zIndex: {
    base: 0,
    dropdown: 100,
    overlay: 1000,
    modal: 1050,
    popover: 1100,
    tooltip: 1200,
  },
}

/**
 * Merges the light/dark mode colors with the shared tokens,
 * returning a complete theme object that matches DefaultTheme.
 */
export const buildTheme = (mode: "light" | "dark"): DefaultTheme => {
  const { modes, ...sharedTokens } = themeSettings
  const modeColors = modes[mode]

  return {
    ...modeColors,
    ...sharedTokens,
  } as DefaultTheme
}

export default themeSettings
